import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useArchiveProperty } from '@/routes/properties/useProperty';

/**
 * Archive confirmation for a property (M1-05's scope extension) — the second
 * planned caller of `ConfirmDialog`, per its own comment when it was
 * extracted for M1-30's customer archive.
 *
 * Idempotent on the API side (M1-02), same reasoning as
 * `ArchiveCustomerDialog`: this never needs to guard a double click, but the
 * button that opens it is hidden once the property is already archived.
 */
export function ArchivePropertyDialog({
  propertyId,
  propertyLine1,
  onClose,
  onArchived,
}: {
  propertyId: string;
  propertyLine1: string;
  onClose: () => void;
  onArchived: () => void;
}) {
  const { t } = useTranslation();
  const archiveProperty = useArchiveProperty(propertyId);

  return (
    <ConfirmDialog
      titleId="archive-property-title"
      title={t('propertyDetail.archiveProperty.title')}
      description={t('propertyDetail.archiveProperty.description', { line1: propertyLine1 })}
      confirmLabel={
        archiveProperty.isPending
          ? t('propertyDetail.archiveProperty.archiving')
          : t('propertyDetail.archiveProperty.confirm')
      }
      isPending={archiveProperty.isPending}
      errorMessage={archiveProperty.isError ? t('propertyDetail.archiveProperty.failed') : undefined}
      testId="archive-property-dialog"
      onClose={onClose}
      onConfirm={() =>
        archiveProperty.mutate(undefined, {
          onSuccess: () => {
            onArchived();
            onClose();
          },
        })
      }
    />
  );
}
